import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { Search, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { globalTaskService } from '@/services/globalTaskService'
import PriorityBadge from '@/components/ProjectCentral/PriorityBadge'
import TaskDisplayId from '@/components/ProjectCentral/TaskDisplayId'

const GlobalTaskSearch = forwardRef((props, ref) => {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const containerRef = useRef(null)
  const inputRef = useRef(null)

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus()
  }))

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => clearTimeout(timer)
  }, [query])

  // Query
  const { data, isLoading } = useQuery(
    ['task-search', debouncedQuery],
    () => globalTaskService.searchTasks(debouncedQuery),
    { enabled: debouncedQuery.length > 0, keepPreviousData: true }
  )

  const tasks = data?.data?.tasks || []

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelect = (task) => {
    setOpen(false)
    setQuery('')
    setDebouncedQuery('')
    navigate(`/projects/${task.project?.id || task.project_id}/tasks/${task.id}`)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
    }
  }

  return (
    <div ref={containerRef} className="relative w-72">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input
        ref={inputRef}
        placeholder="Search tasks..."
        className="pl-8 h-9"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => { if (query.trim()) setOpen(true) }}
        onKeyDown={handleKeyDown}
      />

      {open && debouncedQuery.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-md z-50 max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No tasks found
            </div>
          ) : (
            tasks.map((task) => {
              const assignee = task.assignee
              const name = assignee?.employee
                ? `${assignee.employee.first_name} ${assignee.employee.last_name}`
                : assignee?.email || ''
              return (
                <button
                  key={task.id}
                  onClick={() => handleSelect(task)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent transition-colors text-sm"
                >
                  {/* Project color dot */}
                  <span
                    className="w-2.5 h-2.5 rounded shrink-0"
                    style={{ backgroundColor: task.project?.color || '#3B82F6' }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <TaskDisplayId projectId={task.project?.id || task.project_id} projectKey={task.project?.key} taskId={task.id} taskNumber={task.task_number} taskType={task.type} />
                      <p className="font-medium truncate">{task.title}</p>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{task.project?.name}</p>
                  </div>
                  <PriorityBadge priority={task.priority} showLabel={false} />
                  {assignee && (
                    <Avatar className="h-5 w-5 shrink-0">
                      <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                        {name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
})

GlobalTaskSearch.displayName = 'GlobalTaskSearch'

export default GlobalTaskSearch
